import React, { useState, useEffect, useMemo, useRef } from "react";
// Importações do TanStack Table (incluindo as novas para o filtro de select)
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    flexRender,
} from "@tanstack/react-table";

// Seus imports de serviço e módulos
import { pega_ranking_soma_bolsa, pega_matriz_curricular } from "../../services/api";
import { ConverteSerie } from "./modulo";

// Imports do React-Bootstrap
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Nav from 'react-bootstrap/Nav';

// NOVO COMPONENTE DE FILTRO: Combobox com Checkboxes
// Este componente é mais amigável para seleção múltipla.
const CheckboxDropdownFilter = ({ column }) => {
    const { getFacetedUniqueValues, getFilterValue, setFilterValue } = column;

    // Estado para controlar a visibilidade do dropdown
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null); // Ref para o container principal do filtro

    const facetedValues = getFacetedUniqueValues();
    const sortedUniqueValues = useMemo(
        () => Array.from(facetedValues.keys()).sort(),
        [facetedValues]
    );

    // O valor do filtro é um array, ou um array vazio se indefinido
    const filterValues = (getFilterValue() || []);

    // Hook para fechar o dropdown ao clicar fora dele
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);


    // Função chamada ao marcar/desmarcar um checkbox
    const handleCheckboxChange = (value) => {
        // Copia o array de filtros atual
        const newFilterValues = [...filterValues];

        if (newFilterValues.includes(value)) {
            // Se o valor já existe, remove-o
            const index = newFilterValues.indexOf(value);
            if (index > -1) {
                newFilterValues.splice(index, 1);
            }
        } else {
            // Se não existe, adiciona-o
            newFilterValues.push(value);
        }

        // Atualiza o filtro na tabela. Se o array estiver vazio, passa undefined para limpar.
        setFilterValue(newFilterValues.length > 0 ? newFilterValues : undefined);
    };

    // Texto a ser exibido no botão do filtro
    const getButtonText = () => {
        if (filterValues.length === 0) {
            return "Todas";
        }
        if (filterValues.length === 1) {
            return filterValues[0];
        }
        return `${filterValues.length} selecionadas`;
    };


    return (
        <div ref={wrapperRef} className="checkbox-dropdown-filter" style={{ position: 'relative', marginTop: '5px' }}>
            {/* Botão que abre o dropdown */}
            <button
                type="button"
                className="btn btn-light btn-sm w-100"
                onClick={() => setIsOpen(!isOpen)}
            >
                {getButtonText()} <span style={{ float: 'right' }}>▾</span>
            </button>

            {/* O menu dropdown com os checkboxes */}
            {isOpen && (
                <div
                    className="checkbox-dropdown-menu"
                    onClick={e => e.stopPropagation()} // Impede que o menu feche ao clicar dentro dele
                >
                    {sortedUniqueValues.map(value => (
                        <div key={value} className="dropdown-item">
                            <input
                                type="checkbox"
                                id={`checkbox-${value}`}
                                checked={filterValues.includes(value)}
                                onChange={() => handleCheckboxChange(value)}
                            />
                            <label htmlFor={`checkbox-${value}`}>
                                {value} ({facetedValues.get(value)})
                            </label>
                        </div>
                    ))}
                    {filterValues.length > 0 && (
                        <button
                            className="btn btn-link btn-sm mt-2"
                            onClick={() => setFilterValue(undefined)}
                        >
                            Limpar seleção
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const MultiSelectColumnFilter = ({ column }) => {
    const { getFacetedUniqueValues, getFilterValue, setFilterValue } = column;
    const facetedValues = getFacetedUniqueValues();

    // Ordena os valores únicos para exibição consistente
    const sortedUniqueValues = useMemo(
        () => Array.from(facetedValues.keys()).sort(),
        [facetedValues]
    );

    // Converte o valor do filtro (que pode ser undefined) para um array vazio
    const filterValues = (getFilterValue() || []);

    // Manipulador para quando o usuário muda a seleção
    const handleOnChange = (e) => {
        // Pega todos os <option> selecionados
        const selectedOptions = Array.from(e.target.selectedOptions);
        // Extrai seus valores
        const values = selectedOptions.map(option => option.value);
        // Define o valor do filtro. Se nenhum for selecionado, passa undefined para limpar o filtro.
        setFilterValue(values.length ? values : undefined);
    };

    return (
        <select
            multiple // Habilita a seleção múltipla
            value={filterValues} // O valor agora é um array de strings
            onChange={handleOnChange}
            onClick={e => e.stopPropagation()}
            className="form-select form-select-sm"
            style={{ marginTop: '5px', width: '100%', height: '100px' }} // Aumentar a altura para usabilidade
        >
            {/* Não precisa da opção "Todos" em um select múltiplo */}
            {sortedUniqueValues.map(value => (
                <option key={value} value={value}>
                    {value} ({facetedValues.get(value)})
                </option>
            ))}
        </select>
    );
};

// PASSO 1: CRIAR O COMPONENTE DE FILTRO DE SELECT
// Este componente reutilizável irá gerar um dropdown com os valores únicos da coluna.
const SelectColumnFilter = ({ column }) => {
    const facetedValues = column.getFacetedUniqueValues();

    const sortedUniqueValues = useMemo(
        () => Array.from(facetedValues.keys()).sort(),
        [facetedValues]
    );

    return (
        <select
            value={column.getFilterValue() || ''}
            onChange={e => column.setFilterValue(e.target.value || undefined)}
            onClick={e => e.stopPropagation()}
            className="form-select form-select-sm" // Classe Bootstrap para melhor aparência
            style={{ marginTop: '5px', width: '100%' }}
        >
            <option value="">Todos</option>
            {sortedUniqueValues.map(value => (
                <option key={value} value={value}>
                    {value} ({facetedValues.get(value)})
                </option>
            ))}
        </select>
    );
};


// COMPONENTE PRINCIPAL
const Ranking_Alunos_Serie_BolsaTS = (props) => {
    const [data2, setData2] = useState([]);
    const [matrizCurricular, setMatrizCurricular] = useState([]);

    // Suas constantes e props
    const unidades = ['', 'Bento Ribeiro', 'Madureira', 'Santa Cruz', 'Cascadura', 'Taquara', 'Nilopolis', '', '', 'Seropédica', 'Barra da Tijuca', 'Campo Grande', 'Centro de Excelência', 'Mangueira', 'Maricá', 'Ilha do Governador', 'Freguesia', 'Recreio dos Bandeirantes'];
    const perfil = JSON.parse(localStorage.getItem("perfil"));

    // Lógica para buscar dados (sem alterações)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await pega_ranking_soma_bolsa();
                const response2 = await pega_matriz_curricular();
                setData2(response);
                setMatrizCurricular(response2);
            } catch (error) {
                console.error("Erro ao buscar dados:", error);
            }
        };
        fetchData();
    }, []);

    // PASSO 2: PROCESSAR E MEMOIZAR OS DADOS DA API (sem alterações)
    const processedData = useMemo(() => {
        if (!data2.length || !matrizCurricular.length) return [];

        return data2.map(item => {
            let _ser = item.tur.substring(2, 3);
            let _gra = item.tur.substring(0, 1);
            let divisor = matrizCurricular.filter(mc => mc.ser === _ser && mc.gra === _gra && mc.sts === true && (parseInt(mc.dis) < 340 || parseInt(mc.dis) > 357)).length;

            if (divisor === 0) {
                return {
                    unidade: unidades[parseInt(item.unidade)], serie: ConverteSerie(item.serie), matricula: item.mat, nome: item.nome, turma: item.tur, tipoBolsa: item.dsc, bolsa: parseFloat(item.bol),
                    d1: 0, d2: 0, d3: 0, d4: 0, d_anual: 0
                };
            }

            const d1 = parseFloat(((parseFloat(item.c1) / (divisor * 25)) * 100).toFixed(2));
            const d2 = parseFloat(((parseFloat(item.c2) / (divisor * 25)) * 100).toFixed(2));
            const d3 = parseFloat(((parseFloat(item.c3) / (divisor * 25)) * 100).toFixed(2));
            const d4 = parseFloat(((parseFloat(item.c4) / (divisor * 25)) * 100).toFixed(2));
            const d_anual = parseFloat(((parseFloat(item.c1) + parseFloat(item.c2) + parseFloat(item.c3) + parseFloat(item.c4)) / (divisor * 100) * 100).toFixed(2));

            return {
                unidade: unidades[parseInt(item.unidade)], serie: ConverteSerie(item.serie), matricula: item.mat, nome: item.nome, turma: item.tur, tipoBolsa: item.dsc, bolsa: parseFloat(item.bol),
                d1, d2, d3, d4, d_anual
            };
        });
    }, [data2, matrizCurricular]);

    // PASSO 3: DEFINIR COLUNAS E ESPECIFICAR QUAIS PODEM SER FILTRADAS
    const columns = useMemo(() => [
        {
            accessorKey: 'unidade',
            header: 'Unidade',
            enableColumnFilter: true,
            // Utiliza o filtro de select simples já existente
            filterFn: 'equals',
            Filter: ({ column }) => <SelectColumnFilter column={column} />,
        },
        {
            accessorKey: 'serie',
            header: 'Série',
            enableColumnFilter: true,
            // 1. DEFINA A FUNÇÃO DE FILTRO AQUI
            // A função 'arrIncludesSome' verifica se o valor da linha está no array do filtro
            filterFn: 'arrIncludesSome',
            // 2. ASSOCIE O NOVO COMPONENTE DE FILTRO
            Filter: ({ column }) => <CheckboxDropdownFilter column={column} />,
        },
        {
            accessorKey: 'matricula',
            header: 'Matrícula',
            enableColumnFilter: false
        },
        {
            accessorKey: 'nome',
            header: 'Nome',
            enableColumnFilter: false
        },
        {
            accessorKey: 'turma',
            header: 'Turma',
            enableColumnFilter: false
        },
        {
            accessorKey: 'tipoBolsa',
            header: 'Tipo Bolsa',
            enableColumnFilter: true,
            filterFn: 'equals',
            Filter: ({ column }) => <SelectColumnFilter column={column} />,
        },
        {
            accessorKey: 'bolsa',
            header: 'Bolsa',
            cell: info => `${info.getValue()}%`,
            enableColumnFilter: true,
            filterFn: 'equals',
            Filter: ({ column }) => <SelectColumnFilter column={column} />,
        },
        // O restante das colunas permanece igual
        ...['d1', 'd2', 'd3', 'd4', 'd_anual'].map(key => ({
            accessorKey: key,
            header: () => (key === 'd_anual' ? 'D. Anual' : `D. Ciclo ${key.charAt(1)}`),
            cell: info => {
                const value = info.getValue();
                let style = {};
                if (value < 25) style = { backgroundColor: '#f8d7da', color: '#721c24' };
                else if (value < 50) style = { backgroundColor: '#ffeeba', color: '#856404' };
                else if (value < 70) style = { backgroundColor: '#fff3cd', color: '#856404' };
                else if (value < 90) style = { backgroundColor: '#d4edda', color: '#155724' };
                else style = { backgroundColor: '#cce5ff', color: '#004085' };

                return <div style={{ ...style, padding: '8px', borderRadius: '4px', textAlign: 'center' }}>{value}%</div>;
            },
            enableColumnFilter: false,
        }))
    ], []);

    // PASSO 4: INSTANCIAR A TABELA COM OS GETTERS NECESSÁRIOS
    const table = useReactTable({
        data: processedData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    // PASSO 5: CALCULAR LINHA DE MÉDIA (sem alterações)
    const averageRow = useMemo(() => {
        if (table.getRowModel().rows.length === 0) return null;
        const visibleRows = table.getRowModel().rows;
        const numRows = visibleRows.length;
        const totals = visibleRows.reduce((acc, row) => {
            const original = row.original;
            acc.d1 += original.d1; acc.d2 += original.d2; acc.d3 += original.d3; acc.d4 += original.d4; acc.d_anual += original.d_anual;
            return acc;
        }, { d1: 0, d2: 0, d3: 0, d4: 0, d_anual: 0 });
        return {
            d1: (totals.d1 / numRows).toFixed(2), d2: (totals.d2 / numRows).toFixed(2), d3: (totals.d3 / numRows).toFixed(2), d4: (totals.d4 / numRows).toFixed(2), d_anual: (totals.d_anual / numRows).toFixed(2),
        };
    }, [table.getRowModel().rows]);

    // Função Experiment para o menu (sem alterações)
    const Experiment = (Nperfil) => {
        if (Nperfil === "GESTOR")
            return (
                <>
                    <NavDropdown.Item href="/unidade/10">Barra da Tijuca</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/01">Bento Ribeiro</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/11">Campo Grande</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/04">Cascadura</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/16">Freguesia</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/15">Ilha do Governador</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/02">Madureira</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/14">Maricá</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/06">Nilópolis</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/17">Recreio</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/03">Santa Cruz</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/09">Seropédica</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/05">Taquara</NavDropdown.Item>

                </>
            );
        if (Nperfil.startsWith("DIRETOR")) {
            var numerosString = Nperfil.replace("DIRETOR", "").trim();
            var numerosArray = numerosString.split("-");
            var codigoDinamico = numerosArray.map(function (cod_unidade) {
                return (
                    <NavDropdown.Item href={`/unidade/${cod_unidade}`}>
                        {unidades[parseInt(cod_unidade)]}
                    </NavDropdown.Item>
                );
            });

            return (codigoDinamico)
        };
        if (Nperfil.startsWith("SUPERVISOR")) {
            var numerosString = Nperfil.replace("SUPERVISOR", "").trim();
            var numerosArray = numerosString.split("-");
            var codigoDinamico = numerosArray.map(function (cod_unidade) {
                return (
                    <NavDropdown.Item href={`/unidade/${cod_unidade}`}>
                        {unidades[parseInt(cod_unidade)]}
                    </NavDropdown.Item>
                );
            });

            return (codigoDinamico)
        };
    }

    return (
        <div className="Evasão">
            <div className="sticky-top painel">
                <Navbar expand="lg" className="bg-body-tertiary">
                    <Navbar.Brand href="/">
                        <img src="/novo-logo.png" alt="Santa Mônica Rede de Ensino" style={{ width: "40px", height: "40px", marginRight: "10px" }} />
                        <span style={{ color: "#006cb5", fontFamily: "inherit", fontWeight: "bold" }}>Santa Mônica Rede de Ensino</span>
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav">
                        <NavDropdown title="UNIDADES">
                            {perfil && Experiment(perfil[0].perfil)}
                        </NavDropdown>
                        <Nav.Link href={`${process.env.REACT_APP_FRONTEND}/ranking`}>RANKING DE ALUNOS</Nav.Link>
                    </Navbar.Collapse>
                </Navbar>
            </div>

            <center><h1>Ranking de Desempenho dos Alunos</h1></center>
            <br />

            <div style={{ padding: '0 20px', overflowX: 'auto' }}>
                <table className="styled-table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th key={header.id} onClick={header.column.getToggleSortingHandler()}>
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {{ asc: ' 🔼', desc: ' 🔽' }[header.column.getIsSorted()] ?? null}

                                        {/* Renderiza o filtro customizado definido na coluna */}
                                        {header.column.getCanFilter() ? (
                                            <div>
                                                {flexRender(
                                                    header.column.columnDef.Filter,
                                                    { column: header.column }
                                                )}
                                            </div>
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {averageRow && (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'right', fontWeight: 'bold' }}>Média (dos dados filtrados):</td>
                                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{averageRow.d1}%</td>
                                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{averageRow.d2}%</td>
                                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{averageRow.d3}%</td>
                                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{averageRow.d4}%</td>
                                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{averageRow.d_anual}%</td>
                            </tr>
                        )}
                    </tfoot>
                </table>
            </div>

            <style>{`
                .styled-table {
                    border-collapse: collapse;
                    margin: 25px 0;
                    font-size: 0.9em;
                    font-family: sans-serif;
                    min-width: 400px;
                    box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
                    width: 100%;
                }
                .styled-table thead tr {
                    background-color: #006cb5;
                    color: #ffffff;
                    text-align: left;
                }
                .styled-table th,
                .styled-table td {
                    padding: 12px 15px;
                    vertical-align: middle;
                }
                .styled-table tbody tr {
                    border-bottom: 1px solid #dddddd;
                }
                .styled-table tbody tr:nth-of-type(even) {
                    background-color: #f3f3f3;
                }
                .styled-table tbody tr:last-of-type {
                    border-bottom: 2px solid #006cb5;
                }
                .styled-table th {
                    cursor: pointer;
                    user-select: none;
                }
                .styled-table tfoot td {
                    font-weight: bold;
                    background-color: #f3f3f3;
                }
            `}</style>
        </div>
    );
};

export default Ranking_Alunos_Serie_BolsaTS;